import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-semibold transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Solid navy — the one primary action on a screen.
        default: 'bg-navy text-navy-foreground hover:bg-[#2c3f53]',
        // `gold` names are kept so existing call sites resolve; they are navy now.
        gold: 'bg-navy text-navy-foreground hover:bg-[#2c3f53]',
        'ghost-gold': 'text-navy hover:bg-muted',
        'outline-gold': 'border border-navy bg-transparent text-navy hover:bg-muted',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        outline: 'border border-navy/40 bg-transparent text-navy hover:border-navy hover:bg-muted',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-[#e6eaee]',
        ghost: 'hover:bg-muted hover:text-foreground',
        link: 'text-navy underline underline-offset-4 decoration-1',
      },
      size: {
        default: 'h-11 px-5',
        sm: 'h-9 px-4 text-xs',
        lg: 'h-12 px-7 text-[0.95rem]',
        xl: 'h-12 px-7 text-[0.95rem]',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  }
)
Button.displayName = 'Button'

// eslint-disable-next-line react-refresh/only-export-components -- shadcn/ui pattern: buttonVariants (a cva config, not a component) ships beside Button so callers can style non-button elements with the same variants. Splitting it would fork the upstream file for a dev-only Fast Refresh hint.
export { Button, buttonVariants }
